import * as filestack from 'filestack-js';
import { UploadTags } from 'filestack-js';
import { AWS_S3_URL } from 'utils/constants';
import { getFileType } from 'utils/fileHelper';

import { useState } from 'react';

import { useCommunityContext } from 'components/CommunityContext';

interface UploadFile {
  file: File;
  tempId: string;
}

interface UploadOptions {
  tags?: UploadTags;
}

export default function useFileOperations() {
  const { community, filestackSecurity } = useCommunityContext();
  const [isUploading, setIsUploading] = useState(false);

  const filestackClient = filestack.init(process.env.FILESTACK_API_KEY);

  const generatePreviews = (files: File[]) => {
    const previews = files.map((file) => ({
      fileName: file.name,
      fileSizeInBytes: file.size,
      isLoading: true,
      url: URL.createObjectURL(file),
      attachmentType: getFileType(file.type),
      // Add a temporary ID to help with replacement later
      tempId: Math.random().toString(36).substring(2),
    }));
    return previews;
  };

  const uploadFiles = async (filesToUpload: UploadFile[], options?: UploadOptions) => {
    setIsUploading(true);

    try {
      const storageOptions = {
        access: 'public',
        container: process.env.S3_IMPORT_BUCKET,
        location: 's3',
        path: community.s3UploadPath,
        region: process.env.AWS_REGION,
      };

      const files = filesToUpload.map((file) => file.file);

      const uploadedFiles = await filestackClient.multiupload(
        files,
        {
          ...(options?.tags && { tags: options.tags }),
        },
        storageOptions,
        null,
        filestackSecurity
      );

      const newAttachments = uploadedFiles.map((uploadedFile, index) => ({
        fileName: uploadedFile.filename,
        fileSizeInBytes: uploadedFile.size,
        filestackId: uploadedFile.handle,
        s3Key: uploadedFile.key,
        s3Bucket: uploadedFile.container,
        url: `https://${uploadedFile.container}.${AWS_S3_URL}/${uploadedFile.key}`,
        attachmentUrl: '', // only for video attachments
        attachmentType: getFileType(uploadedFile.mimetype),
        tempId: filesToUpload[index].tempId,
      }));

      setIsUploading(false);
      return { attachments: newAttachments, error: null };
    } catch (error) {
      setIsUploading(false);
      return { attachments: [], error };
    }
  };

  const uploadChatFile = async (file: File, tags?: Record<string, string>) => {
    // Remove blank spaces from file name, Stream breaks if there are spaces in the file name
    const safeFileName = file.name.replace(/\s+/g, '');
    const safeFile = new File([file], safeFileName, { type: file.type });

    const filesToUpload = [
      {
        file: safeFile,
        tempId: Math.random().toString(36).substring(2),
      },
    ];

    const { attachments, error } = await uploadFiles(filesToUpload, { tags });

    if (error) {
      throw error;
    }

    const uploadedFile = attachments[0];

    // Return the format expected by GetStream
    return {
      file: uploadedFile.url,
      mime_type: file.type,
      file_size: file.size,
    };
  };

  return {
    isUploading,
    generatePreviews,
    uploadFiles,
    uploadChatFile,
  };
}
